import React, { useEffect, useState } from 'react';
import { FieldCard } from './FieldCard';
import { useNavigate } from 'react-router-dom';
import { PostLoginWrapper } from './PostLoginWrapper';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

export const Fields = () => {
    const navigate = useNavigate();
    const [fields, setFields] = useState([]);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [trigger, setTrigger] = useState(false);
    const [visibleCount, setVisibleCount] = useState(9);

    // Estado para la imagen y para controlar la visibilidad del Dialog
    const [selectedApplied, setSelectedApplied] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [isImageLoading, setIsImageLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
    
            try {
                const accessToken = localStorage.getItem('accessToken');
                if (!accessToken) {
                    handleLogout();
                    return;
                }
    
                const response = await fetch('/waatic/get_waatic_data', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                });
    
                const data = await response.json();
    
                switch (response.status) {
                    case 200:
                        setFields(data.data);
                        break;
    
                    case 401:
                        const newAccessToken = await refreshAccessToken();
                        if (newAccessToken) {
                            localStorage.setItem('accessToken', newAccessToken);
                            setTrigger(prev => !prev);
                        } else {
                            handleLogout();
                        }
                        break;
    
                    case 403:
                        handleLogout();
                        break;
    
                    default:
                        setError(data.error || 'Error desconocido');
                }
    
            } catch (error) {
                setError('Error de conexión: ' + error.message);
            } finally {
                setIsLoading(false);
            }
        };
    
        fetchData();
    }, [trigger]);

    const refreshAccessToken = async () => {
        try {
            const refreshToken = localStorage.getItem('refreshToken');
            const response = await fetch('/waatic/refresh_token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ refreshToken })
            });

            if (response.ok) {
                const data = await response.json();
                return data.accessToken;
            } else {
                return null;
            }
        } catch (error) {
            console.error("Error al renovar el token de acceso:", error);
            return null;
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
    };

    const loadMoreFields = () => {
        setVisibleCount(prevCount => prevCount + 9);
    };

    // Lógica para cargar la imagen al hacer clic en la tarjeta
    const handleCardClick = async (imageId, applied) => {
        setIsImageLoading(true);
        setSelectedApplied(applied);
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
                handleLogout();
                return;
            }
    
            const response = await fetch(
                `/waatic/get_imagen?id=${imageId}&date=${new Date().getTime()}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );
    
            if (response.headers.get('content-type')?.includes('image')) {
                const imageBlob = await response.blob();
                const imageObjectURL = URL.createObjectURL(imageBlob);
                setSelectedImage(imageObjectURL);
                setError('');
                setIsDialogVisible(true);
                return;
            }
    
            const data = await response.json();
    
            switch (response.status) {
                case 401:
                    const newAccessToken = await refreshAccessToken();
                    if (newAccessToken) {
                        localStorage.setItem('accessToken', newAccessToken);
                        handleCardClick(imageId, applied);
                    } else {
                        handleLogout();
                    }
                    break;
    
                case 403:
                    handleLogout();
                    break;

                case 404:
                    setError('');
                    setTimeout(() => {
                        setError('Imagen no disponible actualmente');
                    }, 100);
                    break;
    
                default:
                    console.error('Error al obtener la imagen:', data.error);
                    setError(data.error || 'Error desconocido al cargar la imagen');
            }
    
        } catch (error) {
            console.error('Error de red:', error);
            setError('Error de conexión al intentar cargar la imagen');
        } finally {
            setIsImageLoading(false);
        }
    };

    // Función para cerrar el Dialog
    const hideDialog = () => {
        setIsDialogVisible(false);
        setSelectedImage(null);
    };

    return (
        <PostLoginWrapper error={error} isMain={true}>
            {isLoading ? (
                <div className="loading"></div>
            ) : (
                <>
                    {isImageLoading && (
                        <div className="loading-2"></div>
                    )}
                    <div className='w-11 flex flex-column align-items-center'>
                        <div 
                            className='w-full flex mobile-field-list desktop-field-list' 
                            style={{ minHeight: '80vh' }}
                        >
                            {fields.slice(0, visibleCount).map((field) => {
                                const prescription = (
                                    field.id_kind_of_application >= 1 && field.id_kind_of_application <= 4
                                ) ? field.prescription : null;

                                const applied = (
                                    field.id_kind_of_application >= 1 && field.id_kind_of_application <= 4
                                ) ? field.applied : null;

                                return (
                                    <FieldCard 
                                        key={field.id}
                                        field_date={field.date}
                                        application_name={field.kind_of_application}
                                        plot_name={field.plot_name} 
                                        worker_name={field.worker_name}
                                        prescription={prescription}
                                        applied={applied}
                                        r_mean_speed={field.r_mean_speed}
                                        t_mean_speed={field.t_mean_speed}
                                        onClick={() => handleCardClick(field.id, field.applied)}
                                    />
                                );
                            })}
                            {/* Relleno para maquetación */}
                            <div className='w-11' style={{ visibility: 'hidden', width: '450px', height: '0px', maxWidth: '450px' }} />
                            <div className='w-11' style={{ visibility: 'hidden', width: '450px', height: '0px', maxWidth: '450px' }} />
                            <div className='w-11' style={{ visibility: 'hidden', width: '450px', height: '0px', maxWidth: '450px' }} />
                        </div>

                        <Button 
                            label={<><i className="pi pi-plus" style={{ marginRight: '8px' }}></i>Cargar más</>} 
                            onClick={() => {
                                loadMoreFields();
                                document.activeElement.blur();
                            }} 
                            className="w-6" 
                            style={{ marginTop: '20px', marginBottom: '20px', maxWidth: '210px', visibility: visibleCount < fields.length ? 'visible' : 'hidden' }}
                        />
                    </div>
                </>
            )}

            {/* Dialog de PrimeReact para mostrar la imagen */}
            <Dialog
                id='image-dialog'
                header={
                    <div>
                        <div>Trabajo realizado</div>
                        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px', marginLeft: '10px' }}>
                            {selectedApplied != null && (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center'}}>
                                        <div style={{ width: '13px', height: '13px', backgroundColor: 'red', borderRadius: '50%', marginRight: '5px' }}></div>
                                        <span style={{ fontSize: '14px' }}>Por debajo de la dosis correcta</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '2px' }}>
                                        <div style={{ width: '13px', height: '13px', backgroundColor: 'green', borderRadius: '50%', marginRight: '5px' }}></div>
                                        <span style={{ fontSize: '14px' }}>Dosis correcta</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '2px'  }}>
                                        <div style={{ width: '13px', height: '13px', backgroundColor: 'blue', borderRadius: '50%', marginRight: '5px' }}></div>
                                        <span style={{ fontSize: '14px' }}>Por encima de la dosis correcta</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                }
                className='w-11'
                visible={isDialogVisible}
                style={{ maxWidth: '559px' }}
                modal
                onHide={hideDialog}
                focusOnShow={false}
            >
                <img 
                    src={selectedImage} 
                    alt="Imagen PNOA" 
                    style={{ 
                        display: 'block', 
                        margin: '0 auto', 
                        maxWidth: '100%', 
                        maxHeight: '70vh' 
                    }}
                />
            </Dialog>
        </PostLoginWrapper>
    );
};
